// src/components/Seo.jsx
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const DEFAULT_ORIGIN = "https://avadgate.com";
const SITE_ORIGIN = (process.env.REACT_APP_SITE_ORIGIN || DEFAULT_ORIGIN).replace(/\/$/, "");
const IS_OFFICIAL_ORIGIN = SITE_ORIGIN === "https://avadgate.com" || SITE_ORIGIN === "https://avadgate.ae";

const DOMAINS = [
  { origin: "https://avadgate.com", hreflang: "en" },
  { origin: "https://avadgate.ae", hreflang: "en-AE" },
];

const ORGANIZATION_SCHEMA_BASE = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Avad Gate",
};

export default function Seo({ title, description, index = true, image = "/preview.png" }) {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const normalizedPathname = pathname === "/homeLife" ? "/homelife" : pathname;
  const path = normalizedPathname === "/" ? "" : normalizedPathname;
  const canonical = `${SITE_ORIGIN}${path}`;
  const imageUrl = image.startsWith("http") ? image : `${SITE_ORIGIN}${image}`;
  const lang = i18n.language || "en";
  const htmlLang = lang === "ar" ? "ar" : lang === "tr" ? "tr" : SITE_ORIGIN === "https://avadgate.ae" ? "en-AE" : "en";
  const ogLocale = htmlLang === "ar" ? "ar_AR" : htmlLang === "tr" ? "tr_TR" : htmlLang === "en-AE" ? "en_AE" : "en_US";
  const organizationSchema = {
    ...ORGANIZATION_SCHEMA_BASE,
    url: SITE_ORIGIN,
    logo: `${SITE_ORIGIN}/avad.svg`,
  };
  const alternateLinks = IS_OFFICIAL_ORIGIN
    ? [
        ...DOMAINS.map((d) => (
          <link key={d.hreflang} rel="alternate" href={`${d.origin}${path}`} hrefLang={d.hreflang} />
        )),
        <link key="x-default" rel="alternate" href={`${DEFAULT_ORIGIN}${path}`} hrefLang="x-default" />,
      ]
    : [
        <link key={htmlLang} rel="alternate" href={canonical} hrefLang={htmlLang} />,
        <link key="x-default" rel="alternate" href={canonical} hrefLang="x-default" />,
      ];

  return (
    <Helmet>
      <html lang={htmlLang} dir={htmlLang === "ar" ? "rtl" : "ltr"} />
      {index && <link rel="canonical" href={canonical} />}

      {index && alternateLinks}

      <meta name="robots" content={index ? "index,follow" : "noindex,follow"} />
      <title>{title}</title>
      <meta name="description" content={description} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Avad Gate" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={ogLocale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
    </Helmet>
  );
}
